import { useMemo } from 'react'

import {
  CostMessagingCoverageModalData,
  CostMessagingModalContent,
  CoverageType,
  GetCostMessagingModalTextParams,
  SessionCountByProgram,
  SessionLimitByProgram,
  SessionsCoverageText,
} from '@lyrahealth-inc/shared-app-logic'

import { getSessionsCoverageText } from '../customers/util/DefaultCoverageTexts'

interface UseGetCostMessagingInfoProps {
  isCostMessagingEnabled: boolean
  costMessagingModalText?: {
    [key in CoverageType]: (params: GetCostMessagingModalTextParams) => CostMessagingModalContent[]
  }
  coverageModalTitleText?: React.ReactNode
  isHPIExclusive?: boolean
  isEAPExclusive?: boolean
  isDependentDisabled?: boolean
  isCustomerEAPMeds?: boolean
  isCustomerHPIMeds?: boolean
  isCustomerProgramEAPCoaching?: boolean
  isCustomerProgramHPITherapy?: boolean
  isCustomerProgramEAPTherapy?: boolean
  customerDisplayName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  customerSessionsCoverageText?: SessionsCoverageText
}
/**
 * Hook to retrieve cost messaging information related to a customer's provided coverage
 */
export const useGetCostMessagingInfo = ({
  isCostMessagingEnabled,
  costMessagingModalText,
  coverageModalTitleText,
  isHPIExclusive,
  isEAPExclusive,
  isDependentDisabled,
  isCustomerEAPMeds,
  isCustomerHPIMeds,
  isCustomerProgramEAPCoaching,
  isCustomerProgramHPITherapy,
  isCustomerProgramEAPTherapy,
  customerDisplayName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  customerSessionsCoverageText,
}: UseGetCostMessagingInfoProps): CostMessagingCoverageModalData => {
  return useMemo(() => {
    const customerCoverageType = isHPIExclusive
      ? CoverageType.HPI_ONLY
      : isEAPExclusive
      ? CoverageType.EAP_ONLY
      : CoverageType.EAP_AND_HPI

    // Use customer-provided sessionsCoverageText if available, otherwise compute from default logic
    const sessionsCoverageText =
      customerSessionsCoverageText ??
      getSessionsCoverageText({
        coverageType: customerCoverageType,
        isDependentDisabled,
        numberOfSessions: numSessions,
        numberOfSessionsByProgram: numberOfSessionByProgram,
        displayVisitsPerIssuePerYearByProgram,
        isCustomerProgramEAPCoaching,
        isCustomerProgramEAPTherapy,
        isCustomerProgramHPITherapy,
      })

    if (isCostMessagingEnabled) {
      let coverageModalContents: CostMessagingModalContent[] = []

      if (costMessagingModalText) {
        coverageModalContents = costMessagingModalText[customerCoverageType]({
          isDependentDisabled,
          isCustomerEAPMeds,
          isCustomerHPIMeds,
          isCustomerProgramEAPCoaching,
          isCustomerProgramHPITherapy,
          isCustomerProgramEAPTherapy,
        })
      }

      console.debug(
        `Cost messaging modal - is supported configuration (coverageModalContents.length > 0): ${
          coverageModalContents.length > 0
        },
        [customerCoverageType: ${customerCoverageType}],
        [isDependentDisabled: ${isDependentDisabled}],
        [isCustomerEAPMeds: ${isCustomerEAPMeds}],
        [isCustomerHPIMeds: ${isCustomerHPIMeds}],
        [isCustomerProgramEAPCoaching: ${isCustomerProgramEAPCoaching}],
        [isCustomerProgramHPITherapy: ${isCustomerProgramHPITherapy}],
        [isCustomerProgramEAPTherapy: ${isCustomerProgramEAPTherapy}],
        `,
      )

      return {
        coverageModalContents,
        coverageModalTitleText,
        isCostMessagingEnabled,
        costMessagingModalCoverageInfo: { customerCoverageType },
        customerDisplayName,
        numSessions,
        numberOfSessionByProgram,
        displayVisitsPerIssuePerYear,
        displayVisitsPerIssuePerYearByProgram,
        sessionsCoverageText,
      }
    }

    return {
      coverageModalContents: [],
      coverageModalTitleText,
      isCostMessagingEnabled,
      costMessagingModalCoverageInfo: undefined,
      customerDisplayName,
      numSessions,
      numberOfSessionByProgram,
      displayVisitsPerIssuePerYear,
      displayVisitsPerIssuePerYearByProgram,
      isDependentDisabled,
      sessionsCoverageText,
    }
  }, [
    isCostMessagingEnabled,
    coverageModalTitleText,
    customerDisplayName,
    numSessions,
    numberOfSessionByProgram,
    displayVisitsPerIssuePerYear,
    displayVisitsPerIssuePerYearByProgram,
    isDependentDisabled,
    isHPIExclusive,
    isEAPExclusive,
    costMessagingModalText,
    isCustomerEAPMeds,
    isCustomerHPIMeds,
    isCustomerProgramEAPCoaching,
    isCustomerProgramHPITherapy,
    isCustomerProgramEAPTherapy,
    customerSessionsCoverageText,
  ])
}
